import { Component, ViewEncapsulation, HostBinding, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { Observable, Subscription, map, catchError } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { NgFor, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatTooltipModule } from '@angular/material/tooltip';

import { User } from '../../../shared/models/user';
import { UserService } from '../../../shared/services/user.service';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { UNDERSCORE, OrderByDirection, OrderBy } from '../../../shared/utils/constant';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';
import { FastLaneService } from '../../services/fastlane.service';
import { FieldOfFastLaneItems } from '../../models/fastlane.model';
import { FastLaneItems } from '../../models/fastlane-datacenter-config';
import { formatTextDimension } from '../../../shared/utils/number.format';

const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'date';
const sortField = 'customerRequestName';

@Component({
	selector: 'copo-search-fastlane-list',
	templateUrl: './search-fastlane-list.component.html',
	animations: pageTransitions,
	styleUrl: './search-fastlane-list.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		VirtualScrollerDirective,
		CdkScrollable,
		TranslateModule,
		NgFor,
		MatRippleModule,
		MatTooltipModule,
		RouterLink,
		NgIf,
		MatIconModule,
		LoadingSpinnerComponent,
		NumberPipe,
	]
})
export class SearchFastLaneListComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	user: User;
	filter?: string | undefined;

	readonly UNDERSCORE = UNDERSCORE;

	fastlaneItemFields: FieldOfFastLaneItems[] = [
		'customerRequestName',
		'supplierRfqNumber',
		'lastModifiedDate',
		'prototypeName',
		'prototypeStatus',
		'requestedSteelGrade',
		'form',
		'thickness',
		'width',
		'length',
		'requestedQuantity',
		'requestedQuantityUnitDescription',
		'requestedDeliveryDate',
		'id'
	];

	private readonly searchTextSubscription?: Subscription;

	constructor(
		private readonly fastLaneService: FastLaneService,
		private readonly userService: UserService,
		private readonly router: Router,
		private readonly activedRoute: ActivatedRoute
	) {
		this.user = this.userService.user;

		if (!this.router.isActive(
			'search',
			{ paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' }
		)) {
			return;
		}
		this.searchTextSubscription = this.activedRoute.params.subscribe(p => {
			const searchText = <string | undefined>p['query'];
			this.filter = searchText ? this.fastLaneService.createQuickSerachFilter(searchText) : undefined;
		});
	}

	get dataId() {
		return `search-fastlane-list-${
			SORT_WAY
		}-${
			SORT_BY
		}-${
			this.filter
		}`;
	}

	ngOnDestroy() {
		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
	}

	getRequestDimention(fastlane: FastLaneItems) {
		return formatTextDimension(
			fastlane.thickness ? Number(Number(fastlane.thickness).toFixed(2)) : '',
			fastlane.width ? Number(Number(fastlane.width).toFixed(0)) : '',
			fastlane.length ? Number(Number(fastlane.length).toFixed(0)) : '',
		);
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<FastLaneItems[]>) => void) {
		provideData(
			this.fastLaneService.getFastLaneItems(
				this.fastlaneItemFields,
				this.filter,
				index,
				limit,
				new OrderBy(sortField, SORT_WAY),
				false
			).pipe(
				map(res => res.results),
				catchError((err: unknown) => {
					console.error('error retrieving batches', err);
					return [];
				})
			)
		);
	}
}
